/**
 * DocumentList — lists documents for an operation with verify / download / delete actions.
 * RBAC is enforced server-side; this component renders all controls and lets
 * the server reject unauthorised attempts (which surface via toast).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { FileText, CheckCircle2, XCircle, Download, Trash2 } from 'lucide-react';
import { verifyDocument, deleteDocument, documentDownloadUrl } from '@/services/workflowApi';
import { useConnectivity } from '@/hooks/useConnectivity';
import { docVerificationLabelEs } from './labels';

export const DocumentList = ({ documents, getAuthHeaders, onChange, canVerify = true, canDelete = false }) => {
  const { isOffline } = useConnectivity();
  const [busy, setBusy] = useState(null);

  if (!documents || documents.length === 0) {
    return <div className="text-xs text-slate-500 italic">Sin documentos adjuntos</div>;
  }

  const handleVerify = async (id, status) => {
    setBusy(id + status);
    try {
      await verifyDocument(getAuthHeaders, id, status, status === 'REJECTED' ? 'rechazado por operador' : 'OK');
      toast.success(`Documento ${status === 'VERIFIED' ? 'verificado' : 'rechazado'}`);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al verificar');
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este documento? Esta acción no se puede deshacer.')) return;
    setBusy(id + 'del');
    try {
      await deleteDocument(getAuthHeaders, id);
      toast.success('Documento eliminado');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setBusy(null);
    }
  };

  const download = (doc) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${documentDownloadUrl(doc.storage_key)}`;
    // Open in new tab. Auth header is required so we use a token-bearing fetch + blob if needed,
    // but for admin-facing UI the bearer header is in the browser session — we just open the URL.
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="space-y-2" data-testid="document-list">
      {documents.map((d) => (
        <div key={d.id} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-white" data-testid={`document-${d.id}`}>
          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">{d.filename}</p>
            <p className="text-[10px] text-slate-500">
              {d.document_type} · {(d.size_bytes / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleDateString()}
            </p>
          </div>
          <span
            className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ring-1 ring-inset ${
              d.verification_status === 'VERIFIED' ? 'bg-emerald-50 text-emerald-700 ring-emerald-300'
              : d.verification_status === 'REJECTED' ? 'bg-rose-50 text-rose-700 ring-rose-300'
              : 'bg-amber-50 text-amber-700 ring-amber-300'
            }`}
            data-testid={`document-status-${d.id}`}
          >
            {docVerificationLabelEs(d.verification_status)}
          </span>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => download(d)} className="p-1.5 text-slate-500 hover:text-slate-900" title="Descargar" data-testid={`document-download-${d.id}`}>
              <Download className="w-3.5 h-3.5" />
            </button>
            {canVerify && d.verification_status === 'PENDING' && (
              <>
                <button onClick={() => handleVerify(d.id, 'VERIFIED')} disabled={busy === d.id + 'VERIFIED' || isOffline} className="p-1.5 text-emerald-600 hover:text-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed" title={isOffline ? 'Sin conexión' : 'Aprobar'} data-testid={`document-approve-${d.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleVerify(d.id, 'REJECTED')} disabled={busy === d.id + 'REJECTED' || isOffline} className="p-1.5 text-rose-600 hover:text-rose-700 disabled:opacity-30 disabled:cursor-not-allowed" title={isOffline ? 'Sin conexión' : 'Rechazar'} data-testid={`document-reject-${d.id}`}>
                  <XCircle className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {canDelete && (
              <button onClick={() => handleDelete(d.id)} disabled={busy === d.id + 'del' || isOffline} className="p-1.5 text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed" title={isOffline ? 'Sin conexión' : 'Eliminar'} data-testid={`document-delete-${d.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentList;
